import React, { useState } from 'react';
import { Wrench, Database, UserCog, Loader2, X, ChevronDown, Copy, Check, ServerCog, Activity, Trash2, Mail, Cake, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The dev toolbar exposes seeded test credentials and calls the backend
// /api/dev/* endpoints (which are gated behind ENABLE_DEV_TOOLS and return 404
// in production). Only render it during local development, or when explicitly
// opted in via REACT_APP_ENABLE_DEV_TOOLS. Production builds never show it.
const DEV_TOOLS_ENABLED =
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_ENABLE_DEV_TOOLS === 'true';

const DevToolbarPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [checkingDb, setCheckingDb] = useState(false);
  const [ensuringDb, setEnsuringDb] = useState(false);
  const [dbStatus, setDbStatus] = useState(null);
  const [copied, setCopied] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(null);
  const [sendingBirthday, setSendingBirthday] = useState(null);
  const [agingRequests, setAgingRequests] = useState(null);

  const sendTestDigest = async (kind) => {
    setSendingDigest(kind);
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Log in as an admin first to test digests'); return; }
      const me = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => (r.ok ? r.json() : null));
      if (!me?.email) { toast.error('Could not identify the logged-in user'); return; }
      const res = await fetch(`${API_URL}/api/dev/send-test-digest?kind=${kind}&email=${encodeURIComponent(me.email)}`, { method: 'POST' }).then((r) => r.json());
      if (res.ok) toast.success(res.recipients === 0 ? 'No recipients configured for that digest — enable admins/managers/all-staff' : `Test ${kind.replace('_digest', '')} digest sent to ${res.sent}/${res.recipients} recipient(s) (${res.off_count} off)`);
      else toast.error(res.error || 'Failed to send test digest');
    } catch (e) { toast.error('Failed to send test digest'); }
    finally { setSendingDigest(null); }
  };

  const sendTestBirthday = async (offset) => {
    setSendingBirthday(offset);
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Log in as an admin first'); return; }
      const me = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => (r.ok ? r.json() : null));
      if (!me?.email) { toast.error('Could not identify the logged-in user'); return; }
      const res = await fetch(`${API_URL}/api/dev/send-test-birthday?offset=${offset}&email=${encodeURIComponent(me.email)}`, { method: 'POST' }).then((r) => r.json());
      if (res.ok) toast.success(res.recipients === 0 ? 'No birthday recipients configured — enable manager/department/admins/all-staff' : `Test birthday for ${res.sample_person} (${res.birthday_date}, ${res.when_label}) sent to ${res.sent}/${res.recipients}`);
      else toast.error(res.error || 'Failed to send test birthday');
    } catch (e) { toast.error('Failed to send test birthday'); }
    finally { setSendingBirthday(null); }
  };

  /**
   * Wind the clock back on YOUR pending requests.
   *
   * The approval ladder and the nudge are both measured in elapsed hours — nudge at 24,
   * reminders at 48/96/120, escalation at 168. Testing any of it otherwise means either
   * waiting two days or typing SQL at a psql prompt, and Rob's standing rule is that
   * anything you'd actually want to do needs a button rather than a command to remember.
   */
  const ageMyPendingRequests = async (hours) => {
    setAgingRequests(hours);
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Log in first'); return; }
      const me = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => (r.ok ? r.json() : null));
      if (!me?.email) { toast.error('Could not identify the logged-in user'); return; }
      // A dev tool that only says "could not" is useless for diagnosing anything —
      // surface the actual status and detail. 404 here almost always means the backend
      // hasn't been restarted since this endpoint was added.
      const r = await fetch(
        `${API_URL}/api/dev/age-pending-requests?hours=${hours}&email=${encodeURIComponent(me.email)}`,
        { method: 'POST' });
      if (r.status === 404) {
        toast.error('Endpoint not found (404) — restart the backend to pick it up');
        return;
      }
      const res = await r.json().catch(() => null);
      if (!r.ok) {
        toast.error(`Failed (${r.status}): ${res?.detail || res?.message || 'no detail returned'}`);
        return;
      }
      if (res?.success) {
        toast.success(res.requests_aged === 0
          ? 'You have no pending requests to age — book some leave first'
          : `${res.requests_aged} pending request(s) moved ${hours}h into the past. Reload to see it.`);
      } else toast.error(res?.message || 'Could not age the requests');
    } catch (e) {
      toast.error(`Could not reach the backend: ${e.message}`);
    }
    finally { setAgingRequests(null); }
  };

  const handleSeedDatabase = async () => {
    if (!window.confirm('This will reset the database and create fresh test data. Continue?')) {
      return;
    }
    
    setSeeding(true);
    try {
      const response = await fetch(`${API_URL}/api/dev/seed`, {
        method: 'POST',
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        toast.success('Database seeded successfully! Refreshing page...', {
          duration: 3000,
        });
        // Refresh after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        toast.error(`Seed failed: ${data.message}`);
      }
    } catch (error) {
      console.error('Seed error:', error);
      toast.error('Failed to seed database');
    } finally {
      setSeeding(false);
    }
  };

  const handleClearDatabase = async () => {
    if (!window.confirm('This will DELETE all data except the superadmin account. You will need to create a new company and users. Continue?')) {
      return;
    }
    
    setClearing(true);
    try {
      const response = await fetch(`${API_URL}/api/dev/clear`, {
        method: 'POST',
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        toast.success('Database cleared! Superadmin preserved. Refreshing...', {
          duration: 3000,
        });
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        toast.error(`Clear failed: ${data.message}`);
      }
    } catch (error) {
      console.error('Clear error:', error);
      toast.error('Failed to clear database');
    } finally {
      setClearing(false);
    }
  };

  const handleSuperadminLogin = () => {
    // Open new tab with login page and auto-fill credentials
    const loginUrl = `${window.location.origin}/login?dev_superadmin=true`;
    window.open(loginUrl, '_blank');
    
    // Try to copy credentials to clipboard (may fail in some browsers)
    const creds = 'superadmin@holidayhive.co.uk / superadmin123';
    try {
      navigator.clipboard.writeText(creds).then(() => {
        toast.success('Superadmin credentials copied! New tab opened.', {
          description: creds,
        });
      }).catch(() => {
        toast.success('New tab opened!', {
          description: creds,
        });
      });
    } catch {
      toast.success('New tab opened!', {
        description: creds,
      });
    }
  };

  const handleAdminLogin = () => {
    const loginUrl = `${window.location.origin}/login?dev_admin=true`;
    window.open(loginUrl, '_blank');
    
    const creds = 'rob@tektraders.co.uk / adminadmin';
    try {
      navigator.clipboard.writeText(creds).then(() => {
        toast.success('Admin credentials copied! New tab opened.', {
          description: creds,
        });
      }).catch(() => {
        toast.success('New tab opened!', {
          description: creds,
        });
      });
    } catch {
      toast.success('New tab opened!', {
        description: creds,
      });
    }
  };

  const copyCredentials = (email, password, label) => {
    const creds = `${email} / ${password}`;
    try {
      navigator.clipboard.writeText(creds).then(() => {
        setCopied(label);
        setTimeout(() => setCopied(null), 2000);
        toast.success(`${label} credentials copied!`);
      }).catch(() => {
        toast.info(`${label}: ${creds}`);
      });
    } catch {
      toast.info(`${label}: ${creds}`);
    }
  };

  const handleCheckDbStatus = async () => {
    setCheckingDb(true);
    try {
      const response = await fetch(`${API_URL}/api/dev/db-status`);
      const data = await response.json();
      setDbStatus(data.status);
      
      if (data.status === 'running') {
        toast.success('PostgreSQL is running', {
          description: data.message,
        });
      } else {
        toast.warning('PostgreSQL is not running', {
          description: 'Click "Start Database" to start it',
        });
      }
    } catch (error) {
      toast.error('Failed to check database status');
      setDbStatus('error');
    } finally {
      setCheckingDb(false);
    }
  };

  const handleEnsureDb = async () => {
    setEnsuringDb(true);
    try {
      const response = await fetch(`${API_URL}/api/dev/ensure-db`, {
        method: 'POST',
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        setDbStatus('running');
        if (data.was_restarted) {
          toast.success('PostgreSQL started successfully!', {
            description: 'Database is now running',
          });
        } else {
          toast.success('PostgreSQL is already running');
        }
      } else {
        toast.error(`Failed: ${data.message}`);
      }
    } catch (error) {
      toast.error('Failed to start database');
    } finally {
      setEnsuringDb(false);
    }
  };

  return (
    <>
      {/* Floating Dev Button */}
      <div className="fixed top-4 right-4 z-[9999]">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-lg transition-all"
          title="Developer Tools"
        >
          <Wrench className="w-4 h-4" />
          <span className="text-sm font-medium">Dev</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute top-12 right-0 w-72 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl overflow-hidden">
            <div className="p-3 border-b border-zinc-700 flex items-center justify-between">
              <span className="text-sm font-bold text-purple-400">Developer Tools</span>
              <button onClick={() => setIsOpen(false)} className="text-zinc-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Database Controls */}
            <div className="p-3 border-b border-zinc-800 space-y-2">
              <div className="text-xs text-zinc-400 font-medium mb-2">Database</div>
              
              {/* DB Status Check */}
              <div className="flex gap-2">
                <button
                  onClick={handleCheckDbStatus}
                  disabled={checkingDb}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-zinc-700 hover:bg-zinc-600 disabled:bg-zinc-800 text-white rounded-lg transition-colors text-sm"
                >
                  {checkingDb ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Activity className="w-4 h-4" />
                  )}
                  <span>Check Status</span>
                  {dbStatus && (
                    <span className={`w-2 h-2 rounded-full ${dbStatus === 'running' ? 'bg-green-400' : 'bg-red-400'}`} />
                  )}
                </button>
                
                <button
                  onClick={handleEnsureDb}
                  disabled={ensuringDb}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-800 text-white rounded-lg transition-colors text-sm"
                >
                  {ensuringDb ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ServerCog className="w-4 h-4" />
                  )}
                  <span>Start DB</span>
                </button>
              </div>
              
              {/* Seed Database */}
              <button
                onClick={handleSeedDatabase}
                disabled={seeding}
                className="w-full flex items-center gap-3 px-3 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-800 text-white rounded-lg transition-colors"
              >
                {seeding ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Database className="w-4 h-4" />
                )}
                <div className="text-left">
                  <div className="text-sm font-medium">
                    {seeding ? 'Seeding...' : 'Reset & Seed Database'}
                  </div>
                  <div className="text-xs opacity-75">Creates test data (TekTraders)</div>
                </div>
              </button>

              {/* Clear Database */}
              <button
                onClick={handleClearDatabase}
                disabled={clearing}
                className="w-full flex items-center gap-3 px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-800 text-white rounded-lg transition-colors"
              >
                {clearing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                <div className="text-left">
                  <div className="text-sm font-medium">
                    {clearing ? 'Clearing...' : 'Clear Database (Fresh Start)'}
                  </div>
                  <div className="text-xs opacity-75">Keeps superadmin only</div>
                </div>
              </button>
            </div>

            {/* Test digest emails */}
            <div className="p-3 border-b border-zinc-800 space-y-2">
              <div className="text-xs text-zinc-400 font-medium mb-2">Test digest emails (to configured recipients)</div>
              <div className="flex gap-2">
                {[['daily_digest', 'Daily'], ['weekly_digest', 'Weekly'], ['monthly_digest', 'Monthly']].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => sendTestDigest(k)}
                    disabled={!!sendingDigest}
                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-600/50 text-purple-300 rounded-lg transition-colors text-sm disabled:opacity-50"
                  >
                    {sendingDigest === k ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Test birthday reminders */}
            <div className="p-3 border-b border-zinc-800 space-y-2">
              <div className="text-xs text-zinc-400 font-medium mb-2">Test birthday reminders (to configured recipients)</div>
              <div className="flex gap-2">
                {[[7, '7 days before'], [2, '2 days before']].map(([o, label]) => (
                  <button
                    key={o}
                    onClick={() => sendTestBirthday(o)}
                    disabled={sendingBirthday !== null}
                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-pink-600/20 hover:bg-pink-600/30 border border-pink-600/50 text-pink-300 rounded-lg transition-colors text-sm disabled:opacity-50"
                  >
                    {sendingBirthday === o ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cake className="w-4 h-4" />}
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Age your pending requests — for testing the reminder ladder + nudge,
                both of which are measured in elapsed hours. */}
            <div className="p-3 border-b border-zinc-800 space-y-2">
              <div className="text-xs text-zinc-400 font-medium mb-2">
                Age YOUR pending requests (test reminders + nudge)
              </div>
              <div className="flex gap-2">
                {[[25, '25h · unlock nudge'], [50, '50h · 1st reminder'], [170, '170h · escalate']].map(([h, label]) => (
                  <button
                    key={h}
                    onClick={() => ageMyPendingRequests(h)}
                    disabled={agingRequests !== null}
                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-amber-600/20 hover:bg-amber-600/30 border border-amber-600/50 text-amber-300 rounded-lg transition-colors text-xs disabled:opacity-50"
                  >
                    {agingRequests === h ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                    <span>{label}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                Moves your pending requests further into the past, so the app thinks
                they've been waiting. Reload the page afterwards. The hourly job sends
                the actual reminder emails.
              </p>
            </div>

            {/* Quick Logins */}
            <div className="p-3 space-y-2">
              <div className="text-xs text-zinc-400 font-medium mb-2">Quick Login (New Tab)</div>
              
              <button
                onClick={handleSuperadminLogin}
                className="w-full flex items-center gap-3 px-3 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-600/50 text-red-400 rounded-lg transition-colors"
              >
                <UserCog className="w-4 h-4" />
                <div className="text-left flex-1">
                  <div className="text-sm font-medium">Superadmin</div>
                  <div className="text-xs opacity-75">superadmin@holidayhive.co.uk</div>
                </div>
              </button>

              <button
                onClick={handleAdminLogin}
                className="w-full flex items-center gap-3 px-3 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-600/50 text-blue-400 rounded-lg transition-colors"
              >
                <UserCog className="w-4 h-4" />
                <div className="text-left flex-1">
                  <div className="text-sm font-medium">Admin (TekTraders)</div>
                  <div className="text-xs opacity-75">rob@tektraders.co.uk</div>
                </div>
              </button>
            </div>

            {/* Credentials Reference */}
            <div className="p-3 bg-zinc-800/50 border-t border-zinc-700">
              <div className="text-xs text-zinc-400 font-medium mb-2">Copy Credentials</div>
              <div className="space-y-1">
                <div 
                  onClick={() => copyCredentials('superadmin@holidayhive.co.uk', 'superadmin123', 'Superadmin')}
                  className="flex items-center justify-between text-xs text-zinc-300 hover:text-white cursor-pointer p-1 rounded hover:bg-zinc-700"
                >
                  <span>Superadmin: superadmin123</span>
                  {copied === 'Superadmin' ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                </div>
                <div 
                  onClick={() => copyCredentials('rob@tektraders.co.uk', 'adminadmin', 'Admin')}
                  className="flex items-center justify-between text-xs text-zinc-300 hover:text-white cursor-pointer p-1 rounded hover:bg-zinc-700"
                >
                  <span>Admin: adminadmin</span>
                  {copied === 'Admin' ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                </div>
                <div 
                  onClick={() => copyCredentials('sarah@tektraders.co.uk', 'password123', 'Employee')}
                  className="flex items-center justify-between text-xs text-zinc-300 hover:text-white cursor-pointer p-1 rounded hover:bg-zinc-700"
                >
                  <span>Employees: password123</span>
                  {copied === 'Employee' ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const DevToolbar = () => {
  if (!DEV_TOOLS_ENABLED) {
    return null;
  }

  return <DevToolbarPanel />;
};

export default DevToolbar;
